import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import { ValidationError } from "./projectRepository";
import {
  TASK_TEMPLATES,
  recommendedTemplatesForScope,
  selectedTemplatePrerequisites,
  type TaskCategory,
} from "../../domain/taskTemplates";
import {
  computeSchedule,
  wouldCreateCycle,
  type Schedule,
  type ScheduleCalendar,
  type ScheduleTaskInput,
  type TaskStatus,
} from "../../engine/schedule";
import { isIsoDate } from "../../engine/workdays";

/**
 * THE SCHEDULE'S RECORDS (spec §62B).
 *
 * What is stored is what somebody decided or observed: the task, how long
 * they think it takes, what it waits on, when it actually started and
 * finished. Everything else — ready, blocked, float, the critical path, and
 * every calculated date — is computed by the pure engine on demand and
 * stored NOWHERE, so it cannot go stale or disagree with itself.
 */

export type TaskDurationSource = "suggested" | "user";

export interface ProjectTask {
  id: string;
  projectId: string;
  name: string;
  category: string;
  notes: string;
  durationDays: number;
  plannedStart: string | null;
  actualStart: string | null;
  actualFinish: string | null;
  status: TaskStatus;
  featureId: string | null;
  assignee: string;
  /** The library task this came from, when it came from one. */
  templateId: string | null;
  durationSource: TaskDurationSource;
  sortOrder: number;
  archivedAt: string | null;
  dependsOn: string[];
}

export interface TaskInput {
  name: string;
  category: TaskCategory | string;
  notes?: string;
  durationDays: number;
  plannedStart?: string | null;
  featureId?: string | null;
  assignee?: string;
  templateId?: string | null;
  durationSource?: TaskDurationSource;
  sortOrder?: number;
}

interface TaskRow {
  id: string;
  project_id: string;
  name: string;
  category: string;
  notes: string;
  duration_days: number;
  planned_start: string | null;
  actual_start: string | null;
  actual_finish: string | null;
  status: string;
  feature_id: string | null;
  assignee: string;
  template_id: string | null;
  duration_source: string;
  sort_order: number;
  archived_at: string | null;
}

const SELECT = `SELECT id, project_id, name, category, notes, duration_days, planned_start, actual_start,
                       actual_finish, status, feature_id, assignee, template_id, duration_source, sort_order, archived_at
                  FROM project_tasks`;

function validate(input: TaskInput): string[] {
  const problems: string[] = [];
  if (input.name.trim() === "") problems.push("A task needs a name.");
  if (!Number.isInteger(input.durationDays) || input.durationDays < 0) {
    problems.push("A duration is a whole number of days (0 for a milestone).");
  }
  if (input.durationDays > 730) problems.push("That duration is longer than two years.");
  if (input.plannedStart && !isIsoDate(input.plannedStart)) problems.push("That start date is not a date.");
  return problems;
}

export class TaskRepository {
  constructor(private readonly db: DbExecutor) {}

  private map(row: TaskRow, dependsOn: string[]): ProjectTask {
    return {
      id: row.id,
      projectId: row.project_id,
      name: row.name,
      category: row.category,
      notes: row.notes,
      durationDays: Number(row.duration_days),
      plannedStart: row.planned_start,
      actualStart: row.actual_start,
      actualFinish: row.actual_finish,
      status: (row.status as TaskStatus) ?? "not-started",
      featureId: row.feature_id,
      assignee: row.assignee,
      templateId: row.template_id,
      durationSource: row.duration_source === "user" ? "user" : "suggested",
      sortOrder: Number(row.sort_order),
      archivedAt: row.archived_at,
      dependsOn,
    };
  }

  async listByProject(projectId: string): Promise<ProjectTask[]> {
    const rows = await this.db.select<TaskRow>(
      `${SELECT} WHERE project_id = ? AND archived_at IS NULL ORDER BY sort_order, name`,
      [projectId],
    );
    const links = await this.db.select<{ task_id: string; depends_on_id: string }>(
      `SELECT d.task_id, d.depends_on_id FROM task_dependencies d
         JOIN project_tasks t ON t.id = d.task_id
         JOIN project_tasks p ON p.id = d.depends_on_id
        WHERE t.project_id = ? AND t.archived_at IS NULL AND p.archived_at IS NULL
        ORDER BY d.created_at, p.sort_order, p.name`,
      [projectId],
    );
    const byTask = new Map<string, string[]>();
    for (const link of links) {
      const list = byTask.get(link.task_id) ?? [];
      list.push(link.depends_on_id);
      byTask.set(link.task_id, list);
    }
    return rows.map((row) => this.map(row, byTask.get(row.id) ?? []));
  }

  async getById(id: string): Promise<ProjectTask | null> {
    const rows = await this.db.select<TaskRow>(`${SELECT} WHERE id = ?`, [id]);
    if (rows.length === 0) return null;
    const links = await this.db.select<{ depends_on_id: string }>(
      `SELECT d.depends_on_id FROM task_dependencies d
         JOIN project_tasks p ON p.id = d.depends_on_id
        WHERE d.task_id = ? ORDER BY d.created_at, p.sort_order, p.name`,
      [id],
    );
    return this.map(rows[0], links.map((l) => l.depends_on_id));
  }

  async create(projectId: string, input: TaskInput): Promise<ProjectTask> {
    const problems = validate(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const now = new Date().toISOString();
    const id = generateId();
    const sortOrder = input.sortOrder ?? (await this.nextSortOrder(projectId));
    await this.db.execute(
      `INSERT INTO project_tasks (id, project_id, name, category, notes, duration_days, planned_start,
          status, feature_id, assignee, template_id, duration_source, sort_order, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, 'not-started', ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        projectId,
        input.name.trim(),
        input.category,
        input.notes ?? "",
        input.durationDays,
        input.plannedStart ?? null,
        input.featureId ?? null,
        input.assignee ?? "",
        input.templateId ?? null,
        input.durationSource ?? "user",
        sortOrder,
        now,
        now,
      ],
    );
    return (await this.getById(id))!;
  }

  private async nextSortOrder(projectId: string): Promise<number> {
    const rows = await this.db.select<{ n: number | null }>(
      "SELECT MAX(sort_order) AS n FROM project_tasks WHERE project_id = ?",
      [projectId],
    );
    return Number(rows[0]?.n ?? 0) + 10;
  }

  async update(id: string, input: TaskInput): Promise<void> {
    const problems = validate(input);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.db.execute(
      `UPDATE project_tasks SET name = ?, category = ?, notes = ?, duration_days = ?, planned_start = ?,
          feature_id = ?, assignee = ?, duration_source = ?, updated_at = ? WHERE id = ?`,
      [
        input.name.trim(),
        input.category,
        input.notes ?? "",
        input.durationDays,
        input.plannedStart ?? null,
        input.featureId ?? null,
        input.assignee ?? "",
        input.durationSource ?? "user",
        new Date().toISOString(),
        id,
      ],
    );
  }

  /**
   * A duration somebody typed is theirs (spec §62J): it stops being a
   * Berrier Reno suggestion the moment they change it.
   */
  async setDuration(id: string, durationDays: number): Promise<void> {
    if (!Number.isInteger(durationDays) || durationDays < 0) {
      throw new ValidationError(["A duration is a whole number of days (0 for a milestone)."]);
    }
    await this.db.execute(
      "UPDATE project_tasks SET duration_days = ?, duration_source = 'user', updated_at = ? WHERE id = ?",
      [durationDays, new Date().toISOString(), id],
    );
  }

  async setPlannedStart(id: string, date: string | null): Promise<void> {
    if (date !== null && !isIsoDate(date)) throw new ValidationError(["That start date is not a date."]);
    await this.db.execute("UPDATE project_tasks SET planned_start = ?, updated_at = ? WHERE id = ?", [
      date,
      new Date().toISOString(),
      id,
    ]);
  }

  /**
   * Status and the dates that go with it. Completing a task records WHEN,
   * because the schedule of everything after it is computed from that
   * (spec §62G) — and un-completing it takes the finish date away again
   * rather than leaving a date that no longer happened.
   */
  async setStatus(id: string, status: TaskStatus, on?: string): Promise<void> {
    const task = await this.getById(id);
    if (!task) return;
    const today = on && isIsoDate(on) ? on : new Date().toISOString().slice(0, 10);
    const now = new Date().toISOString();
    if (status === "complete") {
      await this.db.execute(
        `UPDATE project_tasks SET status = 'complete', actual_start = COALESCE(actual_start, ?),
            actual_finish = COALESCE(actual_finish, ?), updated_at = ? WHERE id = ?`,
        [task.actualStart ?? today, today, now, id],
      );
      return;
    }
    if (status === "in-progress") {
      await this.db.execute(
        `UPDATE project_tasks SET status = 'in-progress', actual_start = COALESCE(actual_start, ?),
            actual_finish = NULL, updated_at = ? WHERE id = ?`,
        [today, now, id],
      );
      return;
    }
    await this.db.execute(
      "UPDATE project_tasks SET status = 'not-started', actual_start = NULL, actual_finish = NULL, updated_at = ? WHERE id = ?",
      [now, id],
    );
  }

  /** Correcting the record: what actually happened, when it actually did. */
  async setActualDates(id: string, actualStart: string | null, actualFinish: string | null): Promise<void> {
    for (const date of [actualStart, actualFinish]) {
      if (date !== null && !isIsoDate(date)) throw new ValidationError(["That date is not a date."]);
    }
    if (actualStart && actualFinish && actualFinish < actualStart) {
      throw new ValidationError(["A task cannot finish before it started."]);
    }
    await this.db.execute(
      "UPDATE project_tasks SET actual_start = ?, actual_finish = ?, updated_at = ? WHERE id = ?",
      [actualStart, actualFinish, new Date().toISOString(), id],
    );
  }

  async reorder(orderedIds: readonly string[]): Promise<void> {
    await this.db.transaction(async () => {
      const now = new Date().toISOString();
      let order = 10;
      for (const id of orderedIds) {
        await this.db.execute("UPDATE project_tasks SET sort_order = ?, updated_at = ? WHERE id = ?", [order, now, id]);
        order += 10;
      }
    });
  }

  /** Delete = archive: a task that happened is part of the project's history. */
  async archive(id: string): Promise<void> {
    await this.db.transaction(async () => {
      const now = new Date().toISOString();
      await this.db.execute("DELETE FROM task_dependencies WHERE task_id = ? OR depends_on_id = ?", [id, id]);
      await this.db.execute("UPDATE project_tasks SET archived_at = ?, updated_at = ? WHERE id = ?", [now, now, id]);
    });
  }

  async duplicate(id: string): Promise<ProjectTask | null> {
    const task = await this.getById(id);
    if (!task) return null;
    return this.create(task.projectId, {
      name: `${task.name} (copy)`,
      category: task.category,
      notes: task.notes,
      durationDays: task.durationDays,
      featureId: task.featureId,
      assignee: task.assignee,
      durationSource: task.durationSource,
    });
  }

  /* ---------------- dependencies ---------------- */

  /**
   * "This requires that." Refused when it would make a loop — a schedule
   * where A waits on B waits on C waits on A can never start, so it is not
   * a schedule (spec §62C).
   */
  async addDependency(taskId: string, dependsOnId: string): Promise<void> {
    if (taskId === dependsOnId) {
      throw new ValidationError(["A task cannot wait for itself."]);
    }
    const task = await this.getById(taskId);
    if (!task) throw new ValidationError(["That task no longer exists."]);
    const tasks = await this.listByProject(task.projectId);
    if (tasks.some((t) => t.id === taskId && t.dependsOn.includes(dependsOnId))) return;
    if (wouldCreateCycle(tasks.map(toEngineTask), taskId, dependsOnId)) {
      const other = tasks.find((t) => t.id === dependsOnId);
      throw new ValidationError([
        `${task.name} cannot wait for ${other?.name ?? "that task"}: ${other?.name ?? "it"} already waits for ${task.name}, directly or through other tasks.`,
      ]);
    }
    await this.db.execute(
      "INSERT OR IGNORE INTO task_dependencies (task_id, depends_on_id, kind, created_at) VALUES (?, ?, 'finish-start', ?)",
      [taskId, dependsOnId, new Date().toISOString()],
    );
  }

  async removeDependency(taskId: string, dependsOnId: string): Promise<void> {
    await this.db.execute("DELETE FROM task_dependencies WHERE task_id = ? AND depends_on_id = ?", [
      taskId,
      dependsOnId,
    ]);
  }

  /* ---------------- the schedule itself ---------------- */

  async scheduleSettings(projectId: string): Promise<{ startDate: string; calendar: ScheduleCalendar }> {
    const rows = await this.db.select<{ schedule_start_date: string | null; schedule_calendar: string | null; created_at: string }>(
      "SELECT schedule_start_date, schedule_calendar, created_at FROM projects WHERE id = ?",
      [projectId],
    );
    const row = rows[0];
    const fallback = (row?.created_at ?? new Date().toISOString()).slice(0, 10);
    return {
      startDate: row?.schedule_start_date && isIsoDate(row.schedule_start_date) ? row.schedule_start_date : fallback,
      calendar: row?.schedule_calendar === "calendar-days" ? "calendar-days" : "workdays",
    };
  }

  async setScheduleSettings(
    projectId: string,
    settings: { startDate?: string | null; calendar?: ScheduleCalendar },
  ): Promise<void> {
    if (settings.startDate && !isIsoDate(settings.startDate)) {
      throw new ValidationError(["That start date is not a date."]);
    }
    const current = await this.scheduleSettings(projectId);
    await this.db.execute(
      "UPDATE projects SET schedule_start_date = ?, schedule_calendar = ?, updated_at = ? WHERE id = ?",
      [
        settings.startDate ?? current.startDate,
        settings.calendar ?? current.calendar,
        new Date().toISOString(),
        projectId,
      ],
    );
  }

  /** The whole answer for one project: tasks, dates, float, critical path. */
  async schedule(projectId: string, today?: string): Promise<Schedule> {
    return (await this.scheduleWithTasks(projectId, today)).schedule;
  }

  /** Consumers needing task metadata and dates share the same loaded snapshot. */
  async scheduleWithTasks(projectId: string, today?: string): Promise<{ tasks: ProjectTask[]; schedule: Schedule }> {
    const [tasks, settings] = await Promise.all([this.listByProject(projectId), this.scheduleSettings(projectId)]);
    const schedule = computeSchedule(tasks.map(toEngineTask), {
      startDate: settings.startDate,
      calendar: settings.calendar,
      today: today ?? new Date().toISOString().slice(0, 10),
    });
    return { tasks, schedule };
  }

  /* ---------------- building one from the project's scope ---------------- */

  /**
   * What the project's own scope suggests (spec §62I). Nothing is created:
   * the caller shows the suggestion, and only what the user accepts is
   * written.
   */
  async suggestFromScope(projectId: string): Promise<Array<{ templateId: string; name: string; category: string; durationDays: number; requires: string[] }>> {
    const features = await this.db.select<{ feature_definition_id: string | null }>(
      "SELECT DISTINCT feature_definition_id FROM project_features WHERE project_id = ? AND archived_at IS NULL",
      [projectId],
    );
    const objects = await this.db.select<{ definition_id: string }>(
      `SELECT DISTINCT o.definition_id FROM plan_objects o
         JOIN designs d ON d.id = o.design_id
        WHERE d.project_id = ? AND o.archived_at IS NULL AND d.archived_at IS NULL`,
      [projectId],
    );
    const templates = recommendedTemplatesForScope({
      featureDefinitionIds: new Set(features.map((f) => f.feature_definition_id ?? "").filter(Boolean)),
      objectDefinitionIds: new Set(objects.map((o) => o.definition_id)),
    });
    const existing = new Set((await this.listByProject(projectId)).map((t) => t.templateId).filter(Boolean));
    return templates
      .filter((t) => !existing.has(t.id))
      .map((t) => ({
        templateId: t.id,
        name: t.name,
        category: t.category,
        durationDays: t.durationDays,
        requires: [...t.requires],
      }));
  }

  /**
   * Create the accepted suggestions, with the library's own sequencing
   * between them. Everything lands as an ordinary task the user can rename,
   * re-time, re-link or delete.
   */
  async createFromTemplates(projectId: string, templateIds: readonly string[]): Promise<ProjectTask[]> {
    const wanted = TASK_TEMPLATES.filter((t) => templateIds.includes(t.id));
    if (wanted.length === 0) return [];
    return this.db.transaction(async () => {
      const existing = await this.listByProject(projectId);
      const byTemplate = new Map(existing.filter((t) => t.templateId).map((t) => [t.templateId!, t.id]));
      let order = (await this.nextSortOrder(projectId)) - 10;
      const created: ProjectTask[] = [];

      for (const template of wanted) {
        if (byTemplate.has(template.id)) continue;
        order += 10;
        const task = await this.create(projectId, {
          name: template.name,
          category: template.category,
          notes: template.notes ?? "",
          durationDays: template.durationDays,
          templateId: template.id,
          durationSource: "suggested",
          sortOrder: order,
        });
        byTemplate.set(template.id, task.id);
        created.push(task);
      }

      const now = new Date().toISOString();
      const createdIds = new Set(created.map(task => task.id));
      for (const template of wanted) {
        const taskId = byTemplate.get(template.id);
        // Repeated suggestions never reinstate dependencies someone removed
        // or otherwise rewrite the sequencing of an existing task.
        if (!taskId || !createdIds.has(taskId)) continue;
        for (const requires of selectedTemplatePrerequisites(template.id, new Set(byTemplate.keys()))) {
          const dependsOnId = byTemplate.get(requires);
          if (!dependsOnId || dependsOnId === taskId) continue;
          await this.db.execute(
            "INSERT OR IGNORE INTO task_dependencies (task_id, depends_on_id, kind, created_at) VALUES (?, ?, 'finish-start', ?)",
            [taskId, dependsOnId, now],
          );
        }
      }
      return created;
    });
  }
}

export function toEngineTask(task: ProjectTask): ScheduleTaskInput {
  return {
    id: task.id,
    name: task.name,
    durationDays: task.durationDays,
    status: task.status,
    plannedStart: task.plannedStart,
    actualStart: task.actualStart,
    actualFinish: task.actualFinish,
    dependsOn: task.dependsOn,
    sortOrder: task.sortOrder,
  };
}
